import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Newspaper, ExternalLink, Filter } from 'lucide-react';
import { dashboardApi } from '@/api/dashboard';
import type { NewsArticle } from '@/api/types';
import { cn } from '@/lib/cn';
import { ago } from '@/lib/format';

type Relevance = 'all' | '0.3' | '0.5' | '0.7';

const RELEVANCE_VALUES: Record<Relevance, number | undefined> = {
  all: undefined, '0.3': 0.3, '0.5': 0.5, '0.7': 0.7,
};

export function News() {
  const [source, setSource] = useState<string>('');
  const [relevance, setRelevance] = useState<Relevance>('all');
  const [search, setSearch] = useState<string>('');

  const { data: sources } = useQuery({
    queryKey: ['newsSources'],
    queryFn: dashboardApi.newsSources,
    staleTime: 5 * 60_000,
  });

  const { data, isLoading, isError, dataUpdatedAt } = useQuery({
    queryKey: ['news', source, relevance],
    queryFn: () => dashboardApi.news({
      source: source || undefined,
      minRelevance: RELEVANCE_VALUES[relevance],
    }),
    refetchInterval: 60_000,
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter((a) => a.title.toLowerCase().includes(q));
  }, [data, search]);

  return (
    <div className="p-4 lg:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-[10px] bg-surface-2 grid place-items-center text-text-dim">
          <Newspaper size={20} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold">News &amp; Research</h1>
          <div className="text-xs text-text-faint">
            {isLoading ? 'loading…'
              : isError ? 'unable to reach /api/news'
              : `${filtered.length} of ${data?.length ?? 0} articles · refreshed ${ago(new Date(dataUpdatedAt).toISOString())}`}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <Filter size={14} className="text-text-faint" />
        <select
          value={source}
          onChange={(e) => setSource(e.target.value)}
          className="bg-surface-2 border border-[color:var(--border)] rounded-md px-2 py-1 text-xs text-text"
        >
          <option value="">All sources</option>
          {(sources ?? []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <div className="flex gap-1">
          {(['all', '0.3', '0.5', '0.7'] as const).map((r) => (
            <button
              key={r}
              onClick={() => setRelevance(r)}
              className={cn(
                'px-2 py-1 rounded-chip text-xs font-medium border transition',
                relevance === r
                  ? 'bg-kam-violet-soft text-kam-violet border-kam-violet/30'
                  : 'bg-surface-2 text-text-dim border-[color:var(--border)] hover:text-text',
              )}
            >
              {r === 'all' ? 'Any relevance' : `≥ ${r}`}
            </button>
          ))}
        </div>

        <input
          type="search"
          placeholder="Search title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[160px] bg-surface-2 border border-[color:var(--border)] rounded-md px-2 py-1 text-xs text-text placeholder:text-text-faint"
        />
      </div>

      {/* Article list */}
      {filtered.length === 0 && !isLoading
        ? <div className="card card-tight text-xs text-text-faint italic">
            No articles match the current filter. Try widening the relevance threshold or switching source.
          </div>
        : <div className="space-y-2 max-w-3xl">
            {filtered.map((a) => <ArticleCard key={a.id} a={a} />)}
          </div>}
    </div>
  );
}

function ArticleCard({ a }: { a: NewsArticle }) {
  const rel = a.relevanceScore;
  const relTone = rel == null ? 'text-text-faint border-[color:var(--border)] bg-surface-2'
    : rel >= 0.7 ? 'text-kam-green border-kam-green/30 bg-kam-green-soft'
    : rel >= 0.4 ? 'text-kam-amber border-kam-amber/30 bg-kam-amber-soft'
    : 'text-text-dim border-[color:var(--border)] bg-surface-2';
  const when = a.publishedAt ?? a.fetchedAt;

  return (
    <a
      href={a.url}
      target="_blank"
      rel="noopener noreferrer"
      className="card card-tight block hover:border-kam-violet/30 transition group"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] uppercase tracking-wide font-mono text-text-faint">{a.sourceName}</span>
            <span className="text-[10px] text-text-faint">·</span>
            <span className="text-[10px] text-text-faint" title={when}>{ago(when)}</span>
            {!a.processed && (
              <span className="text-[10px] text-text-faint italic">(unscored)</span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-text group-hover:text-kam-violet transition line-clamp-2">
            {a.title}
          </h3>
        </div>
        <div className="flex flex-col items-end gap-2 flex-none">
          <span className={cn(
            'inline-flex items-center px-1.5 py-0.5 rounded-chip text-[10px] font-semibold border tabular-nums font-mono',
            relTone,
          )}>
            {rel != null ? rel.toFixed(2) : '—'}
          </span>
          <ExternalLink size={12} className="text-text-faint group-hover:text-kam-violet" />
        </div>
      </div>
    </a>
  );
}
